import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";
import ReportLayout, { DateRangePicker } from './components/ReportLayout';
import { Users, Building2, ChevronDown } from 'lucide-react';
import useCustomerRetentionReport from '@/hooks/useCustomerRetentionReport';

const PERIOD_MAP = {
  today: 'today',
  yesterday: 'yesterday',
  thisWeek: 'this_week',
  thisMonth: 'this_month',
  lastMonth: 'last_month',
};

export default function NewVsRepeatReport() {
  const { hasPermission } = useAuth();

  if (!hasPermission(P.REPORTS.CUSTOMERS.SCREEN)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Access Denied
          </h3>
          <p className="text-sm text-slate-500">
            You don't have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  const [selectedPreset, setSelectedPreset] = useState('today');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isCustomDate, setIsCustomDate] = useState(false);

  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');

  const sqlPeriod = selectedPreset
    ? PERIOD_MAP[selectedPreset]
    : null;

  const {
    data,
    summary,
    branchSummary,
    loading,
    setFilters,
    refreshCustomerRetention,
  } = useCustomerRetentionReport({
    period: sqlPeriod,
    offset: 0,
    limit: pageSize,
  });

  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [cachedBranchOptions, setCachedBranchOptions] = useState([]);
  useEffect(() => {
    if (branchSummary && branchSummary.length > 1 && !selectedBranchId) {
      setCachedBranchOptions(branchSummary.map(b => ({ id: b.branchId, name: b.branchName || b.branchId })));
    }
  }, [branchSummary, selectedBranchId]);
  const branchOptions = cachedBranchOptions;

  const applyPreset = (preset, page = 1, size = pageSize) => {
  setCurrentPage(page);

  setFilters(prev => ({
    ...prev,
    period: PERIOD_MAP[preset],
    startDate: null,
    endDate: null,
    offset: (page - 1) * size,
    limit: size,
    sortColumn,
    sortDirection,
  }));
};

const applyCustomDate = (page = 1, size = pageSize) => {
  setCurrentPage(page);

  setFilters(prev => ({
    ...prev,
    period: null,
    startDate,
    endDate,
    offset: (page - 1) * size,
    limit: size,
    sortColumn,
    sortDirection,
  }));
};


  const exportToCsv = (filename, columns, rows) => {
    if (!rows?.length) return;

    const headers = columns.map(c => `"${c.label}"`).join(',');
    const csvRows = rows.map(r =>
      columns.map(c => r[c.key] ?? '').join(',')
    );

    const blob = new Blob([headers, '\n', ...csvRows.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const handleExport = () => {
    exportToCsv('new-vs-repeat-customers.csv', columns, data);
  };

  const summaryCards = useMemo(() => [
    {
      title: 'New Customers',
      value: (summary?.newCustomers ?? 0).toLocaleString(),
      icon: Users,
    },
    {
      title: 'Repeat Customers',
      value: (summary?.repeatCustomers ?? 0).toLocaleString(),
      icon: Users,
    },
    {
      title: 'New Revenue',
      value: `₹${(summary?.newRevenue ?? 0).toLocaleString()}`,
      icon: Users,
    },
    {
      title: 'Repeat Revenue',
      value: `₹${(summary?.repeatRevenue ?? 0).toLocaleString()}`,
      icon: Users,
    },
  ], [summary]);

  const columns = [
    {
      key: 'date',
      label: 'Date',
      sortable: true,
      render: v =>
        new Date(v).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
    },
    { key: 'newCustomers', label: 'New', sortable: true, align: 'right' },
    {
      key: 'newRevenue',
      label: 'New Revenue',
      sortable: true,
      align: 'right',
      render: v => `₹${v.toLocaleString()}`,
    },
    { key: 'repeatCustomers', label: 'Repeat', sortable: true, align: 'right' },
    {
      key: 'repeatRevenue',
      label: 'Repeat Revenue',
      sortable: true,
      align: 'right',
      render: v => `₹${v.toLocaleString()}`,
    },
    { key: 'totalCustomers', label: 'Total', sortable: true, align: 'right' },
    {
      key: 'repeatRate',
      label: 'Repeat %',
      sortable: true,
      align: 'right',
      render: v => `${v}%`,
    },
  ];
  const filters = (
  <div className="flex items-end gap-3 flex-wrap">
    <DateRangePicker
      startDate={startDate}
      endDate={endDate}
      selectedPreset={selectedPreset}
      onPresetSelect={(preset) => {
        setSelectedPreset(preset);
        setIsCustomDate(false);
        setCurrentPage(1);
        applyPreset(preset);
      }}
      onStartDateChange={(d) => {
        setStartDate(d);
        setSelectedPreset(null);
        setIsCustomDate(true);
      }}
      onEndDateChange={(d) => {
        setEndDate(d);
        setSelectedPreset(null);
        setIsCustomDate(true);
      }}
    />

    <button
      disabled={!isCustomDate}
      onClick={() => applyCustomDate()}
      className={`h-9 px-5 rounded-lg text-sm font-medium ${
        isCustomDate
          ? 'bg-teal-600 text-white hover:bg-teal-700'
          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
      }`}
    >
      Apply Filters
    </button>

    {branchOptions.length > 0 && (
      <div className="relative">
        <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <select
          value={selectedBranchId ?? ''}
          onChange={(e) => {
            const val = e.target.value || null;
            setSelectedBranchId(val);
            setCurrentPage(1);
            setFilters(prev => ({ ...prev, branchId: val || undefined, offset: 0 }));
          }}
          className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
        >
          <option value="">All Branches</option>
          {branchOptions.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      </div>
    )}
  </div>
);

//SORTING

const sortedData = useMemo(() => {
  if (!data || !sortColumn) return data;

  const sorted = [...data];

  sorted.sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    if (sortColumn === 'date') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    }

    return sortDirection === 'asc'
      ? String(aVal ?? '').localeCompare(String(bVal ?? ''))
      : String(bVal ?? '').localeCompare(String(aVal ?? ''));
  });

  return sorted;
}, [data, sortColumn, sortDirection]);

// CLIENT-SIDE FILTERING - Search across all columns
const filteredData = useMemo(() => {
  if (!searchQuery.trim() || !sortedData) return sortedData;
  const query = searchQuery.toLowerCase().trim();
  return sortedData.filter(row => {
    // Search in date (formatted)
    if (row.date) {
      const dateStr = new Date(row.date).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).toLowerCase();
      if (dateStr.includes(query)) return true;
    }
    // Search in numeric fields
    const numericFields = ['newCustomers', 'newRevenue', 'repeatCustomers', 'repeatRevenue', 'totalCustomers', 'repeatRate'];
    for (const field of numericFields) {
      if (row[field] !== undefined && row[field] !== null) {
        if (String(row[field]).includes(query) || Number(row[field]).toLocaleString().includes(query)) return true;
      }
    }
    return false;
  });
}, [sortedData, searchQuery]);

  return (
    <ReportLayout
      title="New vs Repeat Customers"
      description="Customer retention and acquisition metrics"
      icon={Users}
      exportPermission={P.REPORTS.CUSTOMERS.NEWREPEAT_EXPORT}
      color="blue"
      category="Customers"
      categoryId="customer"
      summaryCards={summaryCards}
      columns={columns}
      data={filteredData}
      filters={filters}
      loading={loading}
      pagination={{
        currentPage,
        totalPages: 1,
        totalItems: data.length,
        pageSize,
        onPageChange: setCurrentPage,
        onPageSizeChange: setPageSize,
      }}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={(column) => {
        let nextDirection;
        if (sortColumn === column) {
          nextDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
          nextDirection = column === 'date' ? 'asc' : 'desc';
        }
        setSortColumn(column);
        setSortDirection(nextDirection);
      }}
      onExport={(format) => {
        if (format === 'csv') handleExport();
      }}
      onRefresh={refreshCustomerRetention}
      onSearch={setSearchQuery}
      searchPlaceholder="Search by date, revenue..."
    />
  );
}
